import { useEffect, useState } from 'react'

import { useNotifications } from 'app/application/application'
import { useApi } from 'app/common/application'

import { Signal } from '../types'
import { SignalUsage, SignalUsageQuery, SignalUsageResponse } from '../types/signalUsage'
import { getUsageDateRange, sumSignalUsage } from './signalUsage'
import useTreatments from './useTreatments'

export interface TreatmentUsageRow {
  id: string
  name: string
  clicks: number | null
  conversions: number | null
}

const useSignalTreatmentUsage = (signal: Signal, page = 1, pageSize = 5) => {
  const { getTreatmentsPage } = useTreatments()
  const { create } = useApi()
  const { createNotification, te } = useNotifications()
  const [rows, setRows] = useState<TreatmentUsageRow[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let active = true
    setRows([])
    setLoading(true)
    setFailed(false)
    const load = async () => {
      try {
        const config = signal.config.treatments
        if (!config || (config.scope === 'treatment' && !config.ids.length)) {
          setTotalPages(0)
          return
        }
        const result = await getTreatmentsPage(
          {
            connection: [signal.connection.platform.id],
            ...(config.scope === 'treatment' ? { id: config.ids } : {}),
          },
          page,
          pageSize,
        )
        if (!active) return
        if (!result.ok) throw result.val
        setTotalPages(result.val.pagination.totalPages)
        const range = getUsageDateRange()
        const usageRows: TreatmentUsageRow[] = []
        for (const treatment of result.val.data) {
          if (!active) return
          const entries: SignalUsage[] = []
          if (treatment.adset?.id) {
            for (let page = 1; ; page++) {
              const usage = await create<SignalUsageQuery, SignalUsageResponse>(
                '/signals/usage/query',
                {
                  content: {
                    ...range,
                    connectionId: signal.connection.platform.id,
                    treatment: treatment.adset.id,
                  },
                  pagination: { page, limit: 100 },
                },
              )
              if (!active) return
              if (!usage.ok) throw usage.val
              entries.push(...usage.val.data)
              if (
                usage.val.pagination
                  ? page >= usage.val.pagination.totalPages
                  : usage.val.data.length < 100
              )
                break
            }
          }
          usageRows.push({
            id: treatment.id,
            name: treatment.adset?.name ?? treatment.name,
            ...sumSignalUsage(entries),
          })
        }
        if (active) setRows(usageRows)
      } catch (error: unknown) {
        if (active) {
          setFailed(true)
          createNotification({
            title: te('http.500'),
            message: error instanceof Error ? error.message : String(error),
            type: 'error',
          })
        }
      } finally {
        if (active) setLoading(false)
      }
    }
    void load()
    return () => {
      active = false
    }
  }, [signal, page, pageSize, create, getTreatmentsPage, createNotification, te])

  return { rows, loading, failed, totalPages }
}

export default useSignalTreatmentUsage
